/**
 * Pure WebAudio synthesis for the game's sound effects and background music. Nothing here touches the DOM or
 * global state, so every function works with a live AudioContext or an OfflineAudioContext.
 * No audio files: everything is generated from oscillators and noise.
 */
/** Approximate length in seconds of each effect, used to cap simultaneous voices. */
export const SFX_DURATION = {
    turn: 0.9, log: 0.06, tile_city: 0.3, tile_greenery: 0.35, tile_ocean: 0.6, tile_other: 0.2,
    param_temp: 0.6, param_oxygen: 0.6, param_venus: 0.7, tr: 0.4,
    card: 0.2, draw: 0.25, click: 0.05, confirm: 0.2, cancel: 0.2,
    generation: 1.4, coins: 0.3, milestone: 1.2, award: 1.3, victory: 2.4,
    error: 0.4, leave: 0.6, join: 0.5,
};
export const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);
const noiseBuffers = new WeakMap();
function noiseBuffer(ctx) {
    let b = noiseBuffers.get(ctx);
    if (b === undefined) {
        b = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
        const d = b.getChannelData(0);
        let s = 12345;
        for (let i = 0; i < d.length; i++) {
            s = (s * 1664525 + 1013904223) >>> 0;
            d[i] = s / 2147483648 - 1;
        }
        noiseBuffers.set(ctx, b);
    }
    return b;
}
function tone(ctx, out, o) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(o.f, o.t);
    if (o.f2 !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(o.f2, o.t + o.dur);
    }
    const a = o.attack ?? 0.01;
    g.gain.setValueAtTime(0.0001, o.t);
    g.gain.linearRampToValueAtTime(o.vol, o.t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, o.t + o.dur);
    osc.connect(g);
    g.connect(out);
    osc.start(o.t);
    osc.stop(o.t + o.dur + 0.05);
}
function noise(ctx, out, o) {
    const s = ctx.createBufferSource();
    const flt = ctx.createBiquadFilter();
    const g = ctx.createGain();
    s.buffer = noiseBuffer(ctx);
    s.loop = true;
    flt.type = 'bandpass';
    flt.Q.value = o.q ?? 1;
    flt.frequency.setValueAtTime(o.f, o.t);
    if (o.f2 !== undefined) {
        flt.frequency.exponentialRampToValueAtTime(o.f2, o.t + o.dur);
    }
    g.gain.setValueAtTime(0.0001, o.t);
    g.gain.linearRampToValueAtTime(o.vol, o.t + Math.min(0.02, o.dur / 3));
    g.gain.exponentialRampToValueAtTime(0.0001, o.t + o.dur);
    s.connect(flt);
    flt.connect(g);
    g.connect(out);
    s.start(o.t);
    s.stop(o.t + o.dur + 0.05);
}
function arp(ctx, out, t, notes, gap, dur, vol, type = 'sine') {
    notes.forEach((f, i) => tone(ctx, out, { t: t + i * gap, f, dur, vol, type }));
}
/** Plays one effect starting at time `t` into `out`. `loud` makes the turn chime more noticeable. */
export function playSfx(ctx, out, name, t, loud = false) {
    switch (name) {
        case 'turn': {
            const v = loud ? 0.3 : 0.2;
            arp(ctx, out, t, [783.99, 1046.5, 1318.51], 0.12, 0.7, v);
            tone(ctx, out, { t, f: 1567.98, dur: 0.5, vol: v * 0.25 });
            if (loud) {
                arp(ctx, out, t + 0.75, [783.99, 1046.5, 1318.51], 0.12, 0.7, v * 0.8);
            }
            break;
        }
        case 'log':
            tone(ctx, out, { t, f: 520, dur: 0.05, vol: 0.025 });
            break;
        case 'tile_city':
            tone(ctx, out, { t, f: 200, f2: 120, dur: 0.22, vol: 0.28, type: 'triangle' });
            noise(ctx, out, { t, dur: 0.1, vol: 0.12, f: 900, q: 0.8 });
            tone(ctx, out, { t: t + 0.08, f: 660, dur: 0.22, vol: 0.07 });
            break;
        case 'tile_greenery':
            tone(ctx, out, { t, f: 523.25, f2: 784, dur: 0.25, vol: 0.14, attack: 0.04 });
            tone(ctx, out, { t: t + 0.12, f: 1568, dur: 0.25, vol: 0.05 });
            noise(ctx, out, { t, dur: 0.2, vol: 0.04, f: 4500, q: 1.5 });
            break;
        case 'tile_ocean':
            noise(ctx, out, { t, dur: 0.5, vol: 0.2, f: 1400, f2: 350, q: 0.6 });
            [0.08, 0.18, 0.3].forEach((d, i) => tone(ctx, out, { t: t + d, f: 380 + i * 90, f2: 800 + i * 150, dur: 0.1, vol: 0.06 }));
            break;
        case 'tile_other':
            tone(ctx, out, { t, f: 330, f2: 190, dur: 0.12, vol: 0.24, type: 'triangle' });
            noise(ctx, out, { t, dur: 0.06, vol: 0.1, f: 1500, q: 1 });
            break;
        case 'param_temp':
            tone(ctx, out, { t, f: 220, f2: 440, dur: 0.5, vol: 0.14, attack: 0.05 });
            tone(ctx, out, { t, f: 330, f2: 660, dur: 0.5, vol: 0.06, type: 'triangle', attack: 0.05 });
            break;
        case 'param_oxygen':
            noise(ctx, out, { t, dur: 0.5, vol: 0.1, f: 600, f2: 2600, q: 0.7 });
            tone(ctx, out, { t: t + 0.05, f: 660, f2: 990, dur: 0.4, vol: 0.08 });
            break;
        case 'param_venus':
            arp(ctx, out, t, [880, 1108.73, 1318.51, 1760], 0.08, 0.5, 0.07, 'triangle');
            tone(ctx, out, { t, f: 440, dur: 0.6, vol: 0.05, attack: 0.1 });
            break;
        case 'tr':
            arp(ctx, out, t, [659.25, 880, 1174.66], 0.06, 0.35, 0.1);
            break;
        case 'card':
            noise(ctx, out, { t, dur: 0.14, vol: 0.14, f: 2200, f2: 900, q: 0.8 });
            tone(ctx, out, { t: t + 0.02, f: 700, f2: 340, dur: 0.1, vol: 0.12 });
            break;
        case 'draw':
            noise(ctx, out, { t, dur: 0.06, vol: 0.1, f: 3200, q: 1.2 });
            noise(ctx, out, { t: t + 0.08, dur: 0.06, vol: 0.1, f: 3600, q: 1.2 });
            tone(ctx, out, { t: t + 0.1, f: 960, dur: 0.1, vol: 0.05 });
            break;
        case 'click':
            tone(ctx, out, { t, f: 900, f2: 700, dur: 0.035, vol: 0.05 });
            break;
        case 'confirm':
            arp(ctx, out, t, [660, 880], 0.07, 0.12, 0.09);
            break;
        case 'cancel':
            arp(ctx, out, t, [494, 370], 0.07, 0.12, 0.08);
            break;
        case 'generation':
            [261.63, 329.63, 392, 523.25].forEach((f, i) => tone(ctx, out, { t, f, dur: 1.2 - i * 0.1, vol: 0.09, attack: 0.18 }));
            arp(ctx, out, t + 0.4, [1046.5, 1318.51, 1567.98], 0.14, 0.6, 0.05);
            break;
        case 'coins':
            tone(ctx, out, { t, f: 1568, dur: 0.14, vol: 0.07 });
            tone(ctx, out, { t: t + 0.06, f: 2093, dur: 0.2, vol: 0.07 });
            break;
        case 'milestone':
            arp(ctx, out, t, [523.25, 659.25, 783.99, 1046.5], 0.1, 0.5, 0.12, 'triangle');
            tone(ctx, out, { t: t + 0.4, f: 1318.51, dur: 0.8, vol: 0.09 });
            break;
        case 'award':
            arp(ctx, out, t, [392, 523.25, 659.25, 783.99], 0.12, 0.6, 0.11, 'triangle');
            [523.25, 659.25, 783.99, 1046.5].forEach((f) => tone(ctx, out, { t: t + 0.5, f, dur: 0.8, vol: 0.05, attack: 0.05 }));
            break;
        case 'victory':
            arp(ctx, out, t, [392, 493.88, 587.33, 783.99, 987.77], 0.14, 1.0, 0.13, 'triangle');
            tone(ctx, out, { t: t + 0.7, f: 1174.66, dur: 1.6, vol: 0.09 });
            [392, 587.33, 783.99].forEach((f) => tone(ctx, out, { t: t + 0.7, f, dur: 1.6, vol: 0.05, attack: 0.08 }));
            break;
        case 'error':
            tone(ctx, out, { t, f: 180, f2: 100, dur: 0.35, vol: 0.14 });
            tone(ctx, out, { t: t + 0.02, f: 187, f2: 104, dur: 0.35, vol: 0.12 });
            break;
        case 'leave':
            tone(ctx, out, { t, f: 300, f2: 140, dur: 0.35, vol: 0.12, type: 'triangle' });
            [0.15, 0.3].forEach((d) => tone(ctx, out, { t: t + d + 0.1, f: 120, f2: 80, dur: 0.08, vol: 0.2 }));
            break;
        case 'join':
            arp(ctx, out, t, [587.33, 880], 0.12, 0.35, 0.1);
            break;
    }
}
function mulberry32(seed) {
    let a = seed;
    return () => {
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Slow soft pad: two detuned oscillators per note through a low-pass, long attack and release. */
function pad(ctx, out, wet, o) {
    const flt = ctx.createBiquadFilter();
    const g = ctx.createGain();
    flt.type = 'lowpass';
    flt.frequency.value = o.cutoff ?? 900;
    g.gain.setValueAtTime(0.0001, o.t);
    g.gain.linearRampToValueAtTime(o.vol, o.t + o.dur * 0.35);
    g.gain.linearRampToValueAtTime(o.vol * 0.8, o.t + o.dur * 0.7);
    g.gain.linearRampToValueAtTime(0.0001, o.t + o.dur);
    if (o.sweep) {
        const lfo = ctx.createOscillator();
        const lg = ctx.createGain();
        lfo.frequency.value = 1 / (o.dur * 0.9);
        lg.gain.value = o.sweep;
        lfo.connect(lg);
        lg.connect(flt.frequency);
        lfo.start(o.t);
        lfo.stop(o.t + o.dur + 0.1);
    }
    for (const m of o.midi) {
        const f = midiToFreq(m);
        for (const [type, cents] of [['sine', -4], ['triangle', 4]]) {
            const osc = ctx.createOscillator();
            const og = ctx.createGain();
            osc.type = type;
            osc.frequency.value = f;
            osc.detune.value = cents;
            og.gain.value = type === 'sine' ? 0.6 : 0.25;
            osc.connect(og);
            og.connect(flt);
            osc.start(o.t);
            osc.stop(o.t + o.dur + 0.1);
        }
    }
    flt.connect(g);
    g.connect(out);
    g.connect(wet);
}
/** Soft bell / pluck with a slightly inharmonic overtone and long decay. */
function bell(ctx, out, wet, t, midi, vol, dur, bright = 0.25) {
    const f = midiToFreq(midi);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    for (const [mul, a] of [[1, 1], [2.76, bright], [5.4, bright * 0.2]]) {
        const osc = ctx.createOscillator();
        const og = ctx.createGain();
        osc.frequency.value = f * mul;
        og.gain.value = a;
        osc.connect(og);
        og.connect(g);
        osc.start(t);
        osc.stop(t + dur + 0.05);
    }
    g.connect(out);
    g.connect(wet);
}
function drone(ctx, out, midi, length, vol) {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, 0);
    g.gain.linearRampToValueAtTime(vol, 3);
    g.gain.setValueAtTime(vol, length);
    g.gain.linearRampToValueAtTime(0.0001, length + 3);
    const lfo = ctx.createOscillator();
    const lg = ctx.createGain();
    lfo.frequency.value = 0.125;
    lg.gain.value = vol * 0.3;
    lfo.connect(lg);
    lg.connect(g.gain);
    lfo.start(0);
    lfo.stop(length + 6);
    for (const mul of [1, 2]) {
        const osc = ctx.createOscillator();
        const og = ctx.createGain();
        osc.frequency.value = midiToFreq(midi) * mul;
        og.gain.value = mul === 1 ? 1 : 0.3;
        osc.connect(og);
        og.connect(g);
        osc.start(0);
        osc.stop(length + 6);
    }
    g.connect(out);
}
function wind(ctx, out, t, dur, vol, f1, f2) {
    const s = ctx.createBufferSource();
    const flt = ctx.createBiquadFilter();
    const g = ctx.createGain();
    s.buffer = noiseBuffer(ctx);
    s.loop = true;
    flt.type = 'bandpass';
    flt.Q.value = 1.4;
    flt.frequency.setValueAtTime(f1, t);
    flt.frequency.linearRampToValueAtTime(f2, t + dur);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + dur * 0.45);
    g.gain.linearRampToValueAtTime(0.0001, t + dur);
    s.connect(flt);
    flt.connect(g);
    g.connect(out);
    s.start(t);
    s.stop(t + dur + 0.1);
}
function pick(rand, a) {
    return a[Math.floor(rand() * a.length)];
}
export const MUSIC_TRACKS = [
    {
        // Deep space: low drone, huge slow D-minor chords, sparse high bells.
        id: 'deep',
        length: 48,
        compose(ctx, out, wet, rand) {
            drone(ctx, out, 38, 48, 0.22);
            const chords = [[50, 57, 60, 64], [46, 53, 57, 60], [43, 55, 58, 62], [45, 52, 57, 62], [46, 53, 57, 60], [50, 57, 60, 64]];
            chords.forEach((c, i) => pad(ctx, out, wet, { t: i * 8, midi: c, dur: 11, vol: 0.13, cutoff: 800 }));
            const penta = [74, 77, 79, 81, 84];
            for (let t = 2; t < 46; t += 3 + rand() * 3) {
                bell(ctx, out, wet, t, pick(rand, penta), 0.07, 5);
            }
        },
    },
    {
        // Orbit: F-lydian pads with a slowly sweeping filter and a gentle arpeggio.
        id: 'orbit',
        length: 48,
        compose(ctx, out, wet, rand) {
            const chords = [[53, 57, 60, 64], [52, 55, 60, 67], [50, 57, 60, 65], [46, 53, 57, 62]];
            for (let i = 0; i < 6; i++) {
                const c = chords[i % 4];
                pad(ctx, out, wet, { t: i * 8, midi: c, dur: 11, vol: 0.12, cutoff: 700, sweep: 350 });
                for (let k = 0; k < 10; k++) {
                    bell(ctx, out, wet, i * 8 + k * 0.8 + (rand() < 0.2 ? 0.4 : 0), c[k % 4] + 12 + (k % 8 >= 4 ? 12 : 0), 0.045, 2.4, 0.15);
                }
            }
            drone(ctx, out, 29, 48, 0.12);
        },
    },
    {
        // Mars surface: wind swells, a low drone and slow plucks in A minor pentatonic.
        id: 'mars',
        length: 40,
        compose(ctx, out, wet, rand) {
            drone(ctx, out, 33, 40, 0.2);
            for (let t = 0; t < 38; t += 9) {
                wind(ctx, out, t + rand() * 2, 11, 0.5, 350 + rand() * 250, 700 + rand() * 500);
            }
            const penta = [57, 60, 62, 64, 67, 69, 72];
            for (let t = 3; t < 38; t += 2.5 + rand() * 2.5) {
                bell(ctx, out, wet, t, pick(rand, penta), 0.1, 4, 0.12);
            }
            pad(ctx, out, wet, { t: 0, midi: [45, 52, 57], dur: 20, vol: 0.09, cutoff: 500 });
            pad(ctx, out, wet, { t: 20, midi: [43, 50, 55], dur: 20, vol: 0.09, cutoff: 500 });
        },
    },
    {
        // Starfield: a bright, steady arpeggio of eighth notes over a soft C-lydian pad.
        id: 'stars',
        length: 40,
        compose(ctx, out, wet, rand) {
            const step = 0.3125;
            const chords = [[60, 64, 67, 71, 76], [57, 60, 64, 69, 72], [62, 66, 69, 74, 78], [55, 59, 62, 67, 71]];
            for (let i = 0; i < 8; i++) {
                const c = chords[i % 4];
                pad(ctx, out, wet, { t: i * 5, midi: c.slice(0, 3).map((m) => m - 12), dur: 7, vol: 0.1, cutoff: 650 });
                for (let k = 0; k < 16; k++) {
                    if (rand() < 0.15) {
                        continue;
                    }
                    const m = c[[0, 2, 4, 3, 1, 3, 4, 2][k % 8]] + (k % 8 === 0 ? 0 : 12);
                    bell(ctx, out, wet, i * 5 + k * step, m, k % 4 === 0 ? 0.07 : 0.045, 1.6, 0.1);
                }
            }
        },
    },
];
export const MUSIC_TRACK_IDS = MUSIC_TRACKS.map((t) => t.id);
/**
 * Renders one track into a seamless loop: the reverb tail past the loop end is folded onto the start,
 * and the result is peak-normalised so every track has the same loudness.
 */
export async function renderTrack(track, createOffline, sampleRate = 22050, seed = 1) {
    const tail = 6;
    const oc = createOffline(Math.ceil(sampleRate * (track.length + tail)), sampleRate);
    const out = oc.createGain();
    out.connect(oc.destination);
    const dly = oc.createDelay(1);
    dly.delayTime.value = 0.37;
    const fb = oc.createGain();
    fb.gain.value = 0.42;
    const damp = oc.createBiquadFilter();
    damp.type = 'lowpass';
    damp.frequency.value = 2500;
    dly.connect(damp);
    damp.connect(fb);
    fb.connect(dly);
    const wetGain = oc.createGain();
    wetGain.gain.value = 0.55;
    damp.connect(wetGain);
    wetGain.connect(out);
    const wetIn = oc.createGain();
    wetIn.connect(dly);
    track.compose(oc, out, wetIn, mulberry32(seed));
    const buf = await oc.startRendering();
    const src = buf.getChannelData(0);
    const n = Math.floor(sampleRate * track.length);
    const data = new Float32Array(n);
    for (let i = 0; i < n; i++) {
        data[i] = src[i];
    }
    for (let i = n; i < src.length; i++) {
        data[(i - n) % n] += src[i];
    }
    let peak = 0;
    for (let i = 0; i < n; i++) {
        peak = Math.max(peak, Math.abs(data[i]));
    }
    const k = peak > 0 ? 0.5 / peak : 1;
    for (let i = 0; i < n; i++) {
        data[i] *= k;
    }
    return { data, sampleRate };
}
